/**
 * Co-Browsing Manager (user client) — on-demand rrweb + live CONFIG
 *
 * Destinations (user side only):
 *   SUBSCRIBE /chat/cobrowse/control/{myUserId}
 *   SEND      /app/cobrowse.events/{myUserId}   body: { Events: [...] }
 *
 * Never send before START. Never send empty batches. Cap ≤ 500 events/batch.
 */

const SERVER_MAX_EVENTS_PER_BATCH = 500;
const MAX_BATCH_BYTES = 350 * 1024;
const MAX_BUFFER_EVENTS = 400;

/** rrweb EventType.FullSnapshot */
const RRWEB_FULL_SNAPSHOT = 2;
/** rrweb EventType.Meta */
const RRWEB_META = 4;

const PRESETS = {
    LOW: {
        Preset: "LOW",
        FlushIntervalMs: 900,
        MaxEventsPerBatch: 80,
        MouseMoveThrottleMs: 250,
        ScrollThrottleMs: 200,
        CheckoutEveryNms: 0,
        InlineStylesheet: false,
        RecordCanvas: false,
    },
    MEDIUM: {
        Preset: "MEDIUM",
        FlushIntervalMs: 450,
        MaxEventsPerBatch: 200,
        MouseMoveThrottleMs: 150,
        ScrollThrottleMs: 150,
        CheckoutEveryNms: 0,
        // Keep false by default — inline CSS blows up FullSnapshot size
        InlineStylesheet: false,
        RecordCanvas: false,
    },
    HIGH: {
        Preset: "HIGH",
        // Latency ≈ FlushIntervalMs/2 on a good network — keep this low for "live" feel
        FlushIntervalMs: 150,
        MaxEventsPerBatch: 400,
        MouseMoveThrottleMs: 80,
        ScrollThrottleMs: 80,
        CheckoutEveryNms: 0,
        InlineStylesheet: false,
        RecordCanvas: false,
    },
};

/** Default MEDIUM until admin sends CONFIG */
const DEFAULT_SETTINGS = { ...PRESETS.MEDIUM };

let recordApi = null;
let stopFn = null;
let buffer = [];
let flushTimer = null;
let isLive = false;
let currentSendEvents = null;
let currentUserId = null;
let lastLogAt = 0;
let sending = false;
let settings = { ...DEFAULT_SETTINGS };
/** Optional rrweb sampling overrides from CONFIG.Settings.Sampling */
let samplingOverride = null;

async function ensureRrweb() {
    if (!recordApi) {
        const mod = await import("rrweb");
        recordApi = mod.record;
    }
    return recordApi;
}

function clampBatchSize(n) {
    const v = Number(n);
    if (!Number.isFinite(v) || v <= 0) {
        return Math.min(settings.MaxEventsPerBatch, SERVER_MAX_EVENTS_PER_BATCH);
    }
    return Math.min(Math.floor(v), SERVER_MAX_EVENTS_PER_BATCH);
}

function pickNumber(next, prev, fallback) {
    if (next === null || next === undefined) return prev ?? fallback;
    const v = Number(next);
    return Number.isFinite(v) ? v : (prev ?? fallback);
}

function pickBool(next, prev, fallback) {
    if (next === null || next === undefined) return prev ?? fallback;
    return Boolean(next);
}

function readField(incoming, pascal, camel) {
    if (!incoming) return undefined;
    if (Object.prototype.hasOwnProperty.call(incoming, pascal)) return incoming[pascal];
    if (Object.prototype.hasOwnProperty.call(incoming, camel)) return incoming[camel];
    return undefined;
}

/**
 * Merge CONFIG.Settings — null/undefined keeps previous.
 * Named Preset fills empty fields from PRESETS table.
 */
export function mergeSettings(incoming = {}) {
    const prev = { ...settings };
    const rawPreset = readField(incoming, "Preset", "preset");
    const presetKey = String(rawPreset ?? prev.Preset ?? "MEDIUM").toUpperCase();
    const fromPreset = presetKey !== "CUSTOM" && PRESETS[presetKey] ? PRESETS[presetKey] : null;

    const merged = fromPreset ? { ...prev, ...fromPreset } : { ...prev, Preset: "CUSTOM" };

    const applyNum = (pascal, camel, key) => {
        const v = readField(incoming, pascal, camel);
        if (v === null || v === undefined) {
            if (!fromPreset) merged[key] = prev[key];
            return;
        }
        merged[key] = pickNumber(v, prev[key], DEFAULT_SETTINGS[key]);
    };

    const applyBool = (pascal, camel, key) => {
        const v = readField(incoming, pascal, camel);
        if (v === null || v === undefined) {
            if (!fromPreset) merged[key] = prev[key];
            return;
        }
        merged[key] = pickBool(v, prev[key], DEFAULT_SETTINGS[key]);
    };

    if (rawPreset != null) {
        merged.Preset = fromPreset ? presetKey : "CUSTOM";
    }

    applyNum("FlushIntervalMs", "flushIntervalMs", "FlushIntervalMs");
    applyNum("MaxEventsPerBatch", "maxEventsPerBatch", "MaxEventsPerBatch");
    applyNum("MouseMoveThrottleMs", "mouseMoveThrottleMs", "MouseMoveThrottleMs");
    applyNum("ScrollThrottleMs", "scrollThrottleMs", "ScrollThrottleMs");
    applyNum("CheckoutEveryNms", "checkoutEveryNms", "CheckoutEveryNms");
    applyBool("InlineStylesheet", "inlineStylesheet", "InlineStylesheet");
    applyBool("RecordCanvas", "recordCanvas", "RecordCanvas");

    const sampling = readField(incoming, "Sampling", "sampling");
    if (sampling && typeof sampling === "object") {
        samplingOverride = { ...(samplingOverride || {}), ...sampling };
    }

    merged.MaxEventsPerBatch = clampBatchSize(merged.MaxEventsPerBatch);
    // HIGH can go as low as 100ms; never below that (CPU thrash)
    merged.FlushIntervalMs = Math.max(
        100,
        Math.floor(merged.FlushIntervalMs || DEFAULT_SETTINGS.FlushIntervalMs)
    );

    settings = merged;
    return settings;
}

function approxSize(value) {
    try {
        return JSON.stringify(value).length;
    } catch (e) {
        return Number.MAX_SAFE_INTEGER;
    }
}

function publishChunk(chunk) {
    // Never publish empty / before START
    if (!isLive || !chunk?.length || !currentSendEvents || !currentUserId) return;

    const capped =
        chunk.length > SERVER_MAX_EVENTS_PER_BATCH
            ? chunk.slice(0, SERVER_MAX_EVENTS_PER_BATCH)
            : chunk;

    const bodyLen = approxSize({ Events: capped });
    const now = Date.now();
    if (now - lastLogAt > 2000) {
        lastLogAt = now;
        console.info("[CoBrowse][user] events-send", {
            destination: `/app/cobrowse.events/${currentUserId}`,
            count: capped.length,
            approxBytes: bodyLen,
            buffered: buffer.length,
            preset: settings.Preset,
            flushMs: settings.FlushIntervalMs,
        });
    }

    try {
        currentSendEvents(capped);
    } catch (e) {
        // Drop failed in-flight; do not retry
    }
}

/**
 * At most ONE non-empty batch per tick — avoids flooding the socket.
 */
function flush() {
    if (!isLive || sending || buffer.length === 0 || !currentSendEvents) return;

    sending = true;
    try {
        const maxBatch = clampBatchSize(settings.MaxEventsPerBatch);
        const batch = [];
        let batchBytes = 12;

        while (buffer.length > 0 && batch.length < maxBatch) {
            const event = buffer[0];
            const eventBytes = approxSize(event);
            if (eventBytes > MAX_BATCH_BYTES) {
                buffer.shift();
                console.warn("[CoBrowse][user] drop-oversized-event", {
                    approxBytes: eventBytes,
                    type: event?.type,
                });
                continue;
            }
            const next = batchBytes + eventBytes + (batch.length ? 1 : 0);
            if (batch.length > 0 && next > MAX_BATCH_BYTES) break;
            buffer.shift();
            batch.push(event);
            batchBytes = next;
        }

        if (batch.length > 0) {
            publishChunk(batch);
        }
    } finally {
        sending = false;
    }
}

function restartFlushTimer(intervalMs) {
    if (flushTimer) {
        clearInterval(flushTimer);
        flushTimer = null;
    }
    if (!isLive) return;
    const ms = Math.max(100, Math.floor(intervalMs || settings.FlushIntervalMs));
    flushTimer = setInterval(flush, ms);
}

function pushEvent(event) {
    if (!isLive) return;
    if (buffer.length >= MAX_BUFFER_EVENTS) {
        // Drop oldest incremental noise; keep room for snapshots
        buffer.splice(0, Math.ceil(MAX_BUFFER_EVENTS * 0.25));
    }
    buffer.push(event);

    // First FullSnapshot (and Meta) should leave ASAP so admin player can start
    if (event?.type === RRWEB_FULL_SNAPSHOT || event?.type === RRWEB_META) {
        queueMicrotask(() => {
            if (isLive) flush();
        });
    }
}

function buildRecordOptions() {
    const sampling = {
        mousemove: settings.MouseMoveThrottleMs,
        mouseInteraction: true,
        scroll: settings.ScrollThrottleMs,
        input: "last",
        media: 800,
        ...(samplingOverride || {}),
    };

    const opts = {
        emit(event) {
            pushEvent(event);
        },
        sampling,
        slimDOMOptions: true,
        inlineStylesheet: Boolean(settings.InlineStylesheet),
        inlineImages: false,
        recordCanvas: Boolean(settings.RecordCanvas),
        collectFonts: false,
        maskAllInputs: false,
        maskInputOptions: {
            password: true,
        },
        maskTextSelector:
            '.gp-private, [data-private], input[name*="NationalCode"], input[name*="nationalCode"], input[name*="Card"], input[name*="card"], input[name*="cvv"], input[name*="sheba"], input[name*="shaba"]',
        blockClass: "gp-block-cobrowse",
        blockSelector: settings.RecordCanvas
            ? ".gp-block-cobrowse, [data-cobrowse-block]"
            : ".gp-block-cobrowse, canvas, [data-cobrowse-block]",
    };

    // Periodic FullSnapshot is expensive — only when admin explicitly sets > 0
    if (settings.CheckoutEveryNms > 0) {
        opts.checkoutEveryNms = settings.CheckoutEveryNms;
    }

    return opts;
}

async function startRecorderOnly() {
    const record = await ensureRrweb();
    if (!isLive) return;

    if (stopFn) {
        try {
            stopFn();
        } catch (e) {
            // ignore
        }
        stopFn = null;
    }

    stopFn = record(buildRecordOptions());

    // Force an immediate FullSnapshot so admin gets a first paint quickly
    try {
        if (typeof record.takeFullSnapshot === "function") {
            record.takeFullSnapshot();
        }
    } catch (e) {
        // ignore — emit path will still produce initial snapshot
    }
}

async function silentRestartRecorder() {
    if (!isLive) return;
    try {
        await startRecorderOnly();
        console.info("[CoBrowse][user] recorder-silent-restart", {
            preset: settings.Preset,
            settings,
        });
    } catch (err) {
        console.warn("[CoBrowse][user] recorder-silent-restart-failed", err);
    }
}

function needsRecorderRestart(prev, next) {
    return (
        prev.MouseMoveThrottleMs !== next.MouseMoveThrottleMs ||
        prev.ScrollThrottleMs !== next.ScrollThrottleMs ||
        prev.InlineStylesheet !== next.InlineStylesheet ||
        prev.RecordCanvas !== next.RecordCanvas ||
        prev.CheckoutEveryNms !== next.CheckoutEveryNms
    );
}

/**
 * Apply admin CONFIG for real (not just log).
 * Live: retune flush timer; soft-restart rrweb when sampling/canvas/checkout change.
 * Not live: stash for next START.
 */
export function applyConfig(incomingSettings) {
    const prev = { ...settings };
    const next = mergeSettings(incomingSettings || {});

    console.info("[CoBrowse][user] config-applied", {
        live: isLive,
        prevPreset: prev.Preset,
        nextPreset: next.Preset,
        FlushIntervalMs: next.FlushIntervalMs,
        MaxEventsPerBatch: next.MaxEventsPerBatch,
        MouseMoveThrottleMs: next.MouseMoveThrottleMs,
        ScrollThrottleMs: next.ScrollThrottleMs,
        CheckoutEveryNms: next.CheckoutEveryNms,
        InlineStylesheet: next.InlineStylesheet,
        RecordCanvas: next.RecordCanvas,
    });

    if (!isLive) return;

    if (prev.FlushIntervalMs !== next.FlushIntervalMs) {
        restartFlushTimer(next.FlushIntervalMs);
    }

    // MaxEventsPerBatch is read live inside flush()

    if (needsRecorderRestart(prev, next) || (incomingSettings && (incomingSettings.Sampling || incomingSettings.sampling))) {
        silentRestartRecorder();
    }
}

export async function startRecording({ myUserId, sendEvents }) {
    if (isLive) {
        stopRecording({ keepSettings: true });
    }

    currentSendEvents = sendEvents;
    currentUserId = myUserId;
    buffer = [];
    isLive = true;
    sending = false;
    lastLogAt = 0;

    try {
        await startRecorderOnly();
        if (!isLive) return;
        restartFlushTimer(settings.FlushIntervalMs);
        // Push whatever is already buffered (Meta + FullSnapshot) immediately
        flush();
        console.info("[CoBrowse][user] recording-started", {
            myUserId,
            preset: settings.Preset,
            settings,
        });
    } catch (err) {
        console.warn("[CoBrowse][user] recording-start-failed", err);
        stopRecording({ keepSettings: true });
    }
}

export function stopRecording({ keepSettings = true } = {}) {
    const wasLive = isLive;
    isLive = false;
    currentSendEvents = null;
    currentUserId = null;
    sending = false;

    if (flushTimer) {
        clearInterval(flushTimer);
        flushTimer = null;
    }

    if (stopFn) {
        try {
            stopFn();
        } catch (e) {
            // ignore
        }
        stopFn = null;
    }

    buffer = [];
    if (!keepSettings) {
        settings = { ...DEFAULT_SETTINGS };
        samplingOverride = null;
    }
    if (wasLive) {
        console.info("[CoBrowse][user] recording-stopped");
    }
}

export function handleCoBrowseControl(msg, { myUserId, sendEvents }) {
    if (!msg || !myUserId) return;

    console.info("[CoBrowse][user] control-received", msg);

    const action = String(msg.Action ?? msg.action ?? "").toUpperCase();
    if (action === "START") {
        startRecording({ myUserId, sendEvents });
    } else if (action === "STOP") {
        stopRecording({ keepSettings: true });
    } else if (action === "CONFIG") {
        applyConfig(msg.Settings ?? msg.settings ?? {});
    }
}

export function getCoBrowseSettings() {
    return { ...settings, Sampling: samplingOverride ? { ...samplingOverride } : undefined };
}

if (typeof window !== "undefined") {
    window.addEventListener("pagehide", () => stopRecording({ keepSettings: true }));
    window.addEventListener("beforeunload", () => stopRecording({ keepSettings: true }));
}
